import { Dispatch, SetStateAction, useEffect } from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';

import { IFlatMoTreeNode } from '@interfaces/components/tree';
import { IPresetDTO } from '@interfaces/kpi/dto/kpiDTO';
import { IKPISite, ISelectedOnMapSitesByVendor, TResetSitesAndVendorArgs } from '@interfaces/kpi/kpiCommon';
import { IGroupSelectOptions } from '@components/common/GroupSelect/GroupSelect.types';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { ETechnologiesValues, TVendorsValues } from '@enums/common';
import { EFormFields } from '@enums/kpi-api';

import { prepareSiteWithCells } from './utils';

interface UseParametersInitializationLogicProps {
	allSelectedSitesOnMap?: Array<ISelectedOnMapSitesByVendor>;
	defaultSelectedSitesOnMap?: ISelectedOnMapSitesByVendor;
	initialVendor?: string;
	isApplyTechnologyFromPreset?: boolean;
	isMap?: boolean;
	optionsVendorsKpi: Array<TSelectOptionType & { isDefault: boolean }>;
	vendor: { name?: TVendorsValues; value: TVendorsValues };
	setValue: UseFormSetValue<FieldValues>;
	resetSitesAndVendor: (...params: TResetSitesAndVendorArgs) => void;
	setTreeKpi: Dispatch<SetStateAction<IFlatMoTreeNode[]>>;
	setInitKpiData: (nodes: IFlatMoTreeNode[]) => void;
	setTreeCounters: Dispatch<SetStateAction<IFlatMoTreeNode[]>>;
	setInitCountersData: (nodes: IFlatMoTreeNode[]) => void;
	kpiGroupData: IFlatMoTreeNode[];
	countersGroupData: IFlatMoTreeNode[];
	options5G?:  IGroupSelectOptions[];
	options4GFdd?: IGroupSelectOptions[];
	options4GTdd?: IGroupSelectOptions[];
	clearKpiFields: (isWithVendor?: boolean) => void;
	selectedPreset?: IPresetDTO;
	setSelectedPreset: () => Promise<void>;
	setVendor: Dispatch<SetStateAction<string>>;
}

export const useParametersInitializationLogic = ({
	allSelectedSitesOnMap,
	defaultSelectedSitesOnMap,
	initialVendor,
	isApplyTechnologyFromPreset,
	isMap,
	optionsVendorsKpi,
	vendor,
	setValue,
	resetSitesAndVendor,
	setTreeKpi,
	setInitKpiData,
	setTreeCounters,
	setInitCountersData,
	kpiGroupData,
	countersGroupData,
	options5G,
	options4GFdd,
	options4GTdd,
	clearKpiFields,
	selectedPreset,
	setSelectedPreset,
	setVendor,
}: UseParametersInitializationLogicProps) => {
	useEffect(() => {
		if (!allSelectedSitesOnMap?.length) return;

		setVendor(vendor?.value);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setVendor, vendor?.value]);

	useEffect(() => {
		if (!defaultSelectedSitesOnMap || !isApplyTechnologyFromPreset || !isMap) return;

		resetSitesAndVendor(defaultSelectedSitesOnMap);
	}, [defaultSelectedSitesOnMap, isApplyTechnologyFromPreset, isMap, resetSitesAndVendor]);

	useEffect(() => {
		if (!allSelectedSitesOnMap?.length) return;

		const listOfTechnologiesLength = allSelectedSitesOnMap.map(({ sites }) => sites.length);

		if (listOfTechnologiesLength.every(length => length === 0)) {
			return;
		}

		const selectedSitesOnMapByVendor = allSelectedSitesOnMap.find(site => site.vendor === vendor?.value);

		const preparedSitesOnMap: IKPISite[] = (selectedSitesOnMapByVendor?.sites ?? []).flatMap(site => {
			switch (site.technology) {
				case ETechnologiesValues.T_5G:
					return [prepareSiteWithCells(site, options5G)];
				case ETechnologiesValues.T_4G_TDD:
					return [prepareSiteWithCells(site, options4GTdd)];
				case ETechnologiesValues.T_4G_FDD:
					return [prepareSiteWithCells(site, options4GFdd)];
				default:
					return [];
			}
		});

		if (!preparedSitesOnMap.length) {
			return;
		}

		resetSitesAndVendor({
			vendor: vendor?.value,
			sites: preparedSitesOnMap,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendor?.value, allSelectedSitesOnMap, options5G, options4GTdd, options4GFdd]);

	useEffect(() => {
		if (initialVendor) {
			setValue(EFormFields.vendor, optionsVendorsKpi.find(vendor => vendor.value === initialVendor));

			if (!isMap) {
				clearKpiFields(false);
			}
		}
	}, [initialVendor, optionsVendorsKpi, setValue, clearKpiFields, isMap]);

	useEffect(() => {
		void setSelectedPreset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPreset, vendor?.value]);

	useEffect(() => {
		setTreeKpi(kpiGroupData);
		setInitKpiData(kpiGroupData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kpiGroupData.length, setInitKpiData]);

	useEffect(() => {
		setTreeCounters(countersGroupData);
		setInitCountersData(countersGroupData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [countersGroupData.length, setInitCountersData]);
};
